// FILE: src/pages/OrdersPage.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import ManagerShell from "../components/ManagerShell";
import OrderEditorDrawer from "../components/OrderEditorDrawer";
import { db } from "../firebase";
import useRestaurantContext from "../hooks/useRestaurantContext";
import "../Dashboard.css";
import "../Orders.css";

const ORDER_STATUS_LABELS = {
  pending: "Order placed",
  order_placed: "Order placed",
  order_confirmed: "Order confirmed",
  preparing: "Preparing",

  // Delivery
  ready_for_driver: "Waiting for driver",
  accepted: "Driver accepted",

  // Pickup
  ready_for_pickup: "Ready for pickup",

  on_the_way: "On the way",
  delivered: "Delivered",
  completed: "Completed",
  cancelled: "Cancelled",
};

const statusConfig = {
  pending: { dot: "new" },
  order_placed: { dot: "new" },
  order_confirmed: { dot: "in-progress" },
  preparing: { dot: "in-progress" },
  ready_for_driver: { dot: "completed" },
  accepted: { dot: "in-progress" },
  ready_for_pickup: { dot: "completed" },
  on_the_way: { dot: "in-progress" },
  delivered: { dot: "completed" },
  completed: { dot: "completed" },
  cancelled: { dot: "cancelled" },
};

const getRestaurantOrderActionErrorMessage = (error) => {
  const code = String(error?.code || "").toLowerCase().trim();
  const message = String(error?.message || "").trim();
  const lowerMessage = message.toLowerCase();

  if (
    code.includes("permission-denied") ||
    lowerMessage.includes("permission-denied") ||
    lowerMessage.includes("missing or insufficient permissions")
  ) {
    return "New restaurant order actions are temporarily unavailable right now.";
  }

  return message || "Could not update order. Please try again.";
};

const updateOrderStatus = async (restaurantId, orderId, newStatus) => {
  try {
    const ref = doc(db, "orders", orderId);

    const patch = {
      status: newStatus,
      updatedAt: serverTimestamp(),
    };

    if (newStatus === "ready_for_driver") {
      patch.readyForDriverAt = serverTimestamp();
      patch["customerStatus.ready_for_driver"] = serverTimestamp();
    }

    if (newStatus === "ready_for_pickup") {
      patch.readyForPickupAt = serverTimestamp();
      patch["customerStatus.ready_for_pickup"] = serverTimestamp();
    }

    await updateDoc(ref, patch);
    console.log("Updated order status", { restaurantId, orderId, newStatus });
    return patch;
  } catch (error) {
    console.error("Failed to update order status", error);
    throw error;
  }
};

// ✅ NEW: pickup completion helper
const markPickupCompleted = async (restaurantId, orderId) => {
  try {
    const ref = doc(db, "orders", orderId);

    const patch = {
      // Pickup is finished when customer collects it
      status: "completed",

      // Timestamps (safe even if UI doesn’t use them yet)
      pickedUpAt: serverTimestamp(),
      completedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await updateDoc(ref, patch);

    console.log("Marked pickup order completed", { restaurantId, orderId });
    return patch;
  } catch (error) {
    console.error("Failed to mark pickup completed", error);
    throw error;
  }
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(value || 0);

const escapePrintHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getPrintableItems = (order) => {
  const rawItems =
    order?.items ||
    order?.cartItems ||
    order?.orderItems ||
    order?.products ||
    [];

  return Array.isArray(rawItems) ? rawItems : [];
};

const printOrder = (order) => {
  if (!order) return;

  const printWindow = window.open("", "_blank", "width=460,height=760");
  if (!printWindow) {
    alert("Please allow pop-ups for ManiDine so the order can be printed.");
    return;
  }

  const items = getPrintableItems(order);
  const pickup = isPickupOrder(order);
  const scheduled = isScheduledOrder(order);
  const orderCode = deriveNumericCode(order);
  const customerName =
    order.customerName ||
    order.customer?.name ||
    order.customerEmail ||
    order.userId ||
    "Customer";
  const restaurantName =
    order.restaurantName || order.restaurant?.name || "ManiDine Restaurant";
  const specialInstructions =
    order.specialInstructions ||
    order.instructions ||
    order.orderNotes ||
    order.notes ||
    "";
  const total = order.total ?? order.totalAmount ?? order.subtotal ?? 0;
  const statusLabel = getRestaurantStatusLabel(order);
  const scheduleLine = getScheduleLine(order);
  const scheduledFor = getScheduledForDate(order);
  const scheduledForLabel = scheduledFor
    ? scheduledFor.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : scheduleLine.replace(/^Scheduled:\s*/i, "");

  const itemRows = items.length
    ? items
        .map((item) => {
          const name =
            item?.name ||
            item?.itemName ||
            item?.title ||
            item?.menuItemName ||
            "Item";
          const quantity = Number(item?.quantity ?? item?.qty ?? 1) || 1;
          const unitPrice = Number(
            item?.price ?? item?.unitPrice ?? item?.itemPrice ?? 0
          );
          const lineTotal = Number(
            item?.total ?? item?.lineTotal ?? item?.totalPrice ?? unitPrice * quantity
          );
          const itemNotes =
            item?.specialInstructions || item?.instructions || item?.notes || "";
          const modifiers = Array.isArray(item?.modifiers)
            ? item.modifiers
                .map((m) => m?.name || m?.label || m?.title || m)
                .filter(Boolean)
                .join(", ")
            : "";

          return `
            <div class="item-row">
              <div class="item-main">
                <strong class="item-name">${escapePrintHtml(quantity)} × ${escapePrintHtml(name)}</strong>
                <strong class="item-price">${escapePrintHtml(formatCurrency(lineTotal))}</strong>
              </div>
              ${modifiers ? `<div class="item-note"><strong>Options:</strong> ${escapePrintHtml(modifiers)}</div>` : ""}
              ${itemNotes ? `<div class="item-note important-note">${escapePrintHtml(itemNotes)}</div>` : ""}
            </div>`;
        })
        .join("")
    : '<div class="muted">Item details are not available on this order record.</div>';

  const cancelled = String(order.status || "").toLowerCase() === "cancelled";
  const cutlery = getCutleryLabel(order).replace("Cutlery: ", "");

  printWindow.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>ManiDine Order #${escapePrintHtml(orderCode)}</title>
  <style>
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #fff; color: #000; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 13px;
      line-height: 1.3;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .page-wrap { padding: 10px; }
    .ticket {
      width: 72mm;
      max-width: 100%;
      margin: 0 auto;
      overflow-wrap: anywhere;
      word-break: normal;
    }
    .brand {
      text-align: center;
      font-size: 21px;
      line-height: 1.05;
      font-weight: 900;
      margin: 0 0 2px;
    }
    .restaurant {
      text-align: center;
      font-size: 13px;
      font-weight: 800;
      margin: 0 0 8px;
    }
    .cancelled {
      border: 3px solid #000;
      padding: 5px 4px;
      text-align: center;
      font-size: 20px;
      line-height: 1;
      font-weight: 900;
      margin: 0 0 8px;
    }
    .order-number {
      text-align: center;
      font-size: 27px;
      line-height: 1.05;
      font-weight: 900;
      margin: 7px 0 8px;
    }
    .fulfillment {
      border-top: 2px solid #000;
      border-bottom: 2px solid #000;
      text-align: center;
      padding: 7px 3px;
      margin-bottom: 8px;
    }
    .fulfillment-type {
      font-size: 20px;
      line-height: 1;
      font-weight: 900;
      letter-spacing: 0.4px;
    }
    .fulfillment-time {
      font-size: 16px;
      line-height: 1.15;
      font-weight: 900;
      margin-top: 4px;
    }
    .scheduled-box {
      border: 3px solid #000;
      text-align: center;
      font-size: 16px;
      font-weight: 900;
      padding: 6px 4px;
      margin: 0 0 8px;
    }
    .scheduled-time {
      display: block;
      font-size: 15px;
      margin-top: 2px;
    }
    .meta {
      border-bottom: 1px dashed #555;
      padding: 0 0 7px;
      margin-bottom: 7px;
    }
    .meta-row {
      display: grid;
      grid-template-columns: 68px minmax(0, 1fr);
      column-gap: 7px;
      align-items: start;
      margin: 3px 0;
    }
    .meta-label { font-weight: 400; }
    .meta-value { font-weight: 800; text-align: left; }
    .section-label {
      font-size: 12px;
      font-weight: 900;
      text-transform: uppercase;
      margin-bottom: 3px;
    }
    .items { margin: 4px 0 0; }
    .item-row {
      padding: 8px 0;
      border-bottom: 1px dashed #777;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .item-main {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 8px;
      align-items: start;
    }
    .item-name {
      font-size: 15px;
      line-height: 1.2;
      font-weight: 900;
    }
    .item-price {
      font-size: 13px;
      white-space: nowrap;
      text-align: right;
    }
    .item-note {
      margin: 4px 0 0 13px;
      font-size: 12px;
      line-height: 1.25;
    }
    .important-note {
      font-weight: 900;
      text-transform: none;
    }
    .instructions {
      border: 2px solid #000;
      padding: 7px;
      margin-top: 9px;
      font-size: 13px;
      line-height: 1.3;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .instructions-title {
      display: block;
      font-size: 13px;
      font-weight: 900;
      text-transform: uppercase;
      margin-bottom: 3px;
    }
    .address {
      border-top: 1px dashed #555;
      margin-top: 9px;
      padding-top: 7px;
      line-height: 1.3;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .total {
      display: flex;
      justify-content: space-between;
      gap: 8px;
      border-top: 3px solid #000;
      margin-top: 10px;
      padding-top: 7px;
      font-size: 20px;
      line-height: 1.1;
      font-weight: 900;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .muted { font-size: 11px; color: #333; }
    .footer {
      text-align: center;
      margin-top: 10px;
      font-size: 9px;
      color: #333;
    }
    @page { margin: 3mm; }
    @media print {
      html, body { width: auto; }
      .page-wrap { padding: 0; }
      .ticket { width: 72mm; max-width: 100%; margin: 0 auto; }
    }
    @media screen and (max-width: 300px) {
      .page-wrap { padding: 5px; }
    }
  </style>
</head>
<body>
  <div class="page-wrap">
    <div class="ticket">
      <div class="brand">ManiDine</div>
      <div class="restaurant">${escapePrintHtml(restaurantName)}</div>
      ${cancelled ? '<div class="cancelled">CANCELLED</div>' : ""}
      <div class="order-number">ORDER #${escapePrintHtml(orderCode)}</div>

      ${
        scheduled
          ? `<div class="scheduled-box">SCHEDULED ORDER<span class="scheduled-time">${escapePrintHtml(scheduledForLabel || "Scheduled")}</span></div>`
          : ""
      }

      <div class="fulfillment">
        <div class="fulfillment-type">${pickup ? "PICKUP" : "DELIVERY"}</div>
        <div class="fulfillment-time">${scheduled ? escapePrintHtml(scheduledForLabel || "Scheduled") : escapePrintHtml(scheduleLine)}</div>
      </div>

      <div class="meta">
        <div class="meta-row"><span class="meta-label">Placed</span><span class="meta-value">${escapePrintHtml(formatDateTimeFromOrder(order))}</span></div>
        <div class="meta-row"><span class="meta-label">Customer</span><span class="meta-value">${escapePrintHtml(customerName)}</span></div>
        <div class="meta-row"><span class="meta-label">Status</span><span class="meta-value">${escapePrintHtml(statusLabel)}</span></div>
        ${cutlery ? `<div class="meta-row"><span class="meta-label">Cutlery</span><span class="meta-value">${escapePrintHtml(cutlery)}</span></div>` : ""}
      </div>

      <div class="section-label">Order items</div>
      <div class="items">${itemRows}</div>

      ${specialInstructions ? `<div class="instructions"><span class="instructions-title">Special instructions</span>${escapePrintHtml(specialInstructions)}</div>` : ""}
      ${!pickup && order.deliveryAddress ? `<div class="address"><div class="section-label">Delivery address</div>${escapePrintHtml(order.deliveryAddress)}</div>` : ""}

      <div class="total"><span>TOTAL</span><span>${escapePrintHtml(formatCurrency(total))}</span></div>
      <div class="footer">Printed from ManiDine Restaurant Portal</div>
    </div>
  </div>
  <script>
    window.addEventListener('load', function () {
      setTimeout(function () { window.print(); }, 150);
    });
  <\/script>
</body>
</html>`);

  printWindow.document.close();
};

const toDateSafe = (ts) => {
  if (!ts) return null;

  if (typeof ts.toDate === "function") {
    const d = ts.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const seconds = ts?.seconds ?? ts?._seconds;
  if (typeof seconds === "number" && seconds > 0) {
    const d = new Date(seconds * 1000);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (ts?._methodName === "serverTimestamp") return null;

  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
};

// ✅ "12:01 PM"
const formatTimeOnly = (ts) => {
  const d = toDateSafe(ts);
  if (!d) return "";
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

const pickBestOrderDate = (order) => {
  const candidates = [
    order?.createdAt,
    order?.placedAt,
    order?.updatedAt,
    order?.acceptedAt,
    order?.driverAssignedAt,
    order?.pickedUpAt,
    order?.onTheWayAt,
    order?.deliveredAt,
    order?.completedAt,
  ];

  for (const c of candidates) {
    const d = toDateSafe(c);
    if (d) return d;
  }
  return null;
};

const isToday = (timestampLike) => {
  const date = toDateSafe(timestampLike);
  if (!date) return false;

  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
};

const isOrderOverdue = (order) => {
  const created = pickBestOrderDate(order) || new Date(0);
  if (!created || created.getTime() === 0) return false;

  const now = new Date();
  const minutes = (now.getTime() - created.getTime()) / 60000;

  const status = (order.status || "").toLowerCase();

  if (
    (status === "pending" ||
      status === "order_confirmed" ||
      status === "order_placed") &&
    minutes > 10
  ) {
    return true;
  }
  if (status === "preparing" && minutes > 20) return true;

  return false;
};

const deriveNumericCode = (order) => {
  const raw = order.orderNumber || order.id || "";
  const digitsOnly = raw.replace(/\D/g, "");
  if (digitsOnly) {
    const trimmed = digitsOnly.slice(-5);
    return trimmed.padStart(4, "0");
  }
  if (!raw) return "";
  let hash = 0;
  for (let i = 0; i < raw.length; i += 1) {
    hash = (hash * 31 + raw.charCodeAt(i)) % 100000;
  }
  return String(hash).padStart(4, "0");
};

const formatDateTimeFromOrder = (order) => {
  const date = pickBestOrderDate(order);
  if (!date) return "--";

  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const extractLatLng = (loc) => {
  if (!loc) return { lat: null, lng: null };

  const rawLat =
    loc.lat ??
    loc.latitude ??
    (loc.coords && (loc.coords.lat ?? loc.coords.latitude));
  const rawLng =
    loc.lng ??
    loc.longitude ??
    (loc.coords && (loc.coords.lng ?? loc.coords.longitude));

  const lat = typeof rawLat === "string" ? parseFloat(rawLat) : rawLat;
  const lng = typeof rawLng === "string" ? parseFloat(rawLng) : rawLng;

  return {
    lat: typeof lat === "number" && !Number.isNaN(lat) ? lat : null,
    lng: typeof lng === "number" && !Number.isNaN(lng) ? lng : null,
  };
};

const isPickupOrder = (order) => {
  const ft = String(
    order?.fulfillmentType || order?.deliveryMode || ""
  ).toLowerCase();
  const deliveryOption = String(order?.deliveryOption || "").toLowerCase();
  return ft === "pickup" || deliveryOption === "pickup";
};

// ✅ NEW: scheduled detector
const isScheduledOrder = (order) => {
  const timeMode = String(order?.timeMode || "").toLowerCase();
  return !!order?.isScheduled || timeMode === "later";
};

// ✅ NEW: scheduled time (for reminders / future filtering)
// Requires `scheduledForAt` (Firestore Timestamp) to be present.
const getScheduledForDate = (order) => {
  return toDateSafe(order?.scheduledForAt || order?.scheduledAt || null);
};

// ✅ Scheduled/ASAP label for restaurant list
// Supports multiple customer-app field names safely.
const getScheduleLine = (order) => {
  const timeMode = String(order?.timeMode || "").toLowerCase(); // "asap" | "later"
  const scheduled = !!order?.isScheduled || timeMode === "later";

  // Prefer explicit scheduled labels first
  const label =
    (order?.scheduledTimeLabel ||
      order?.timeLabel ||
      order?.etaLabel ||
      "").trim();

  if (scheduled && label) return `Scheduled: ${label}`;
  if (scheduled) return "Scheduled";

  // Not scheduled → show ASAP (or the saved etaLabel if it contains "ASAP (...)")
  if (label && /^asap/i.test(label)) return label;
  return "ASAP";
};

// ✅ Cutlery display helper (supports current + legacy field names)
const getCutleryLabel = (order) => {
  const v =
    order?.cutleryRequested ??
    order?.cutlery ??
    order?.includeCutlery ??
    order?.utensilsRequested ??
    null;

  if (v === true) return "Cutlery: Yes";
  if (v === false) return "Cutlery: No";
  return "";
};

// ✅ UI-only label when driver accepts (no status change needed)
const getRestaurantStatusLabel = (order) => {
  const key = (order?.status || "").toLowerCase();

  // ✅ Pickup completed: ONE line only
  if (key === "completed" && isPickupOrder(order)) {
    const t = formatTimeOnly(order?.pickedUpAt);
    return t ? `Customer picked up at ${t}` : "Customer picked up";
  }
  if (key === "accepted") {
    const dn = (order?.driverName || "").trim();
    return dn ? `Driver accepted (${dn})` : "Driver accepted";
  }
  if (key === "ready_for_driver") {
    if (order?.driverId) {
      const dn = (order?.driverName || "").trim();
      return dn ? `Driver accepted (${dn})` : "Driver accepted";
    }
    return "Waiting for driver";
  }

  if (key === "ready_for_pickup") return "Ready for pickup";

  return ORDER_STATUS_LABELS[key] || ORDER_STATUS_LABELS.pending;
};

// ✅ UI-only subline
const getRestaurantStatusSubline = (order) => {
  const key = (order?.status || "").toLowerCase();

  // ✅ No subline for pickup completed (avoid double "picked up")
  if (key === "completed" && isPickupOrder(order)) return "";
  
  if (key === "accepted") return "Pickup in progress";
  if (key === "ready_for_driver" && order?.driverId) return "Pickup in progress";
  if (key === "ready_for_pickup") return "Customer pickup";

  return "";
};

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState("");
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [updatingOrderId, setUpdatingOrderId] = useState("");
  const [editingOrder, setEditingOrder] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  // ✅ NEW: scheduled reminder banner
  const [scheduledReminderBanner, setScheduledReminderBanner] = useState("");
  const lastReminderOrderIdRef = useRef(null);

  const { restaurantId, loading: restaurantLoading } =
    useRestaurantContext() || {};

  useEffect(() => {
    if (restaurantLoading) {
      setOrdersLoading(true);
      return () => {};
    }

    if (!restaurantId) {
      console.warn("[OrdersPage] No restaurantId (not logged in?)");
      setOrders([]);
      setOrdersLoading(false);
      return;
    }

    const ordersRef = collection(db, "orders");
    const ordersQuery = query(
      ordersRef,
      where("restaurantId", "==", restaurantId)
    );

    const unsubscribe = onSnapshot(
      ordersQuery,
      (snapshot) => {
        const data = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        data.sort((a, b) => {
          const da = pickBestOrderDate(a) || new Date(0);
          const dbb = pickBestOrderDate(b) || new Date(0);
          return dbb - da;
        });

        setOrders(data);
        setOrdersError("");
        setOrdersLoading(false);
      },
      (err) => {
        console.error("Error loading orders", err);
        setOrdersError("Failed to load orders.");
        setOrdersLoading(false);
      }
    );

    return () => unsubscribe();
  }, [restaurantId, restaurantLoading]);

  const overdueCount = useMemo(
    () => orders.filter((o) => isOrderOverdue(o)).length,
    [orders]
  );

  // ✅ NEW: scheduled (future) count for badge
  const scheduledFutureCount = useMemo(() => {
    const now = new Date();
    return (orders || []).filter((o) => {
      const st = String(o.status || "").toLowerCase();
      if (st === "completed" || st === "cancelled") return false;
      if (!isScheduledOrder(o)) return false;

      const d = getScheduledForDate(o);
      // If missing scheduledForAt, still count it as scheduled (we can’t know future/past reliably)
      if (!d) return true;

      return d.getTime() > now.getTime();
    }).length;
  }, [orders]);

  // ✅ NEW: scheduled reminders (1 hour before)
  useEffect(() => {
    if (!restaurantId) return undefined;

    const runReminderCheck = async () => {
      try {
        const now = new Date();

        // Find the closest upcoming scheduled order within 60 minutes
        // Only works if scheduledForAt exists.
        const candidates = (orders || [])
          .filter((o) => {
            const st = String(o.status || "").toLowerCase();
            if (st === "completed" || st === "cancelled") return false;
            if (!isScheduledOrder(o)) return false;

            // already reminded → skip
            if (o.reminderSentAt) return false;

            const d = getScheduledForDate(o);
            if (!d) return false;

            const diffMin = (d.getTime() - now.getTime()) / 60000;
            return diffMin <= 60 && diffMin > 59;
          })
          .sort((a, b) => {
            const da = getScheduledForDate(a)?.getTime() || 0;
            const dbb = getScheduledForDate(b)?.getTime() || 0;
            return da - dbb;
          });

        if (candidates.length === 0) return;

        const order = candidates[0];

        // Prevent double firing in the same minute even before Firestore updates propagate
        if (lastReminderOrderIdRef.current === order.id) return;
        lastReminderOrderIdRef.current = order.id;

        const when = getScheduledForDate(order);
        const whenStr = when
          ? when.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
          : "";

        setScheduledReminderBanner(
          `Reminder: Scheduled order #${deriveNumericCode(order)} is coming up in ~1 hour${whenStr ? ` (at ${whenStr})` : ""}.`
        );

        // Optional: browser notification (safe fallback)
        try {
          if ("Notification" in window) {
            if (Notification.permission === "granted") {
              new Notification("ManiDine — Scheduled order reminder", {
                body: `Order #${deriveNumericCode(order)} is scheduled in ~1 hour${whenStr ? ` (at ${whenStr})` : ""}.`,
              });
            } else if (Notification.permission === "default") {
              // Don't spam permission prompt; only request once a session.
              // You can remove this if you don't want any prompt.
              Notification.requestPermission().catch(() => {});
            }
          }
        } catch (e) {
          // ignore
        }

        // Mark as reminded in Firestore so it never repeats
        try {
          const ref = doc(db, "orders", order.id);
          await updateDoc(ref, {
            reminderSentAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        } catch (e) {
          console.log("[ScheduledReminder] could not write reminderSentAt:", e);
        }
      } catch (err) {
        console.log("[ScheduledReminder] error:", err);
      }
    };

    // Run once shortly after load, then every minute
    const t = setTimeout(() => runReminderCheck(), 1200);
    const interval = setInterval(() => runReminderCheck(), 60 * 1000);

    return () => {
      clearTimeout(t);
      clearInterval(interval);
    };
  }, [orders, restaurantId]);

  const filteredOrders = useMemo(() => {
    const matchesFilter = (order) => {
      const status = (order.status || "").toLowerCase();
      switch (filter) {
        case "scheduled": {
          // Scheduled tab = scheduled orders that are not completed/cancelled,
          // and if scheduledForAt exists, must be in the future.
          if (status === "completed" || status === "cancelled") return false;
          if (!isScheduledOrder(order)) return false;

          const d = getScheduledForDate(order);
          if (!d) return true; // we still show it, since order is scheduled (but timestamp missing)
          return d.getTime() > Date.now();
        }
        case "unfulfilled":
          return status !== "completed" && status !== "cancelled";
        case "today":
          return isToday(pickBestOrderDate(order));
        case "in_progress":
          return [
            "pending",
            "order_confirmed",
            "preparing",
            "ready_for_driver",
            "accepted",
            "ready_for_pickup",
            "on_the_way",
            "delivered",
            "order_placed",
          ].includes(status);
        case "completed":
          return status === "completed";
        case "all":
        default:
          return true;
      }
    };

    const base = orders.filter(matchesFilter);

    if (!searchTerm.trim()) return base;

    const term = searchTerm.trim().toLowerCase();
    return base.filter((order) => {
      const code = deriveNumericCode(order);
      return (
        order.orderNumber?.toLowerCase().includes(term) ||
        code.includes(term) ||
        order.customerName?.toLowerCase().includes(term)
      );
    });
  }, [orders, filter, searchTerm]);

  const renderStatusCell = (order, overdue = false) => {
    const key = (order?.status || "").toLowerCase();
    const meta = statusConfig[key] || statusConfig.pending;

    const label = getRestaurantStatusLabel(order);
    const subline = getRestaurantStatusSubline(order);

    return (
      <span className="order-status">
        <span className={`status-dot ${meta.dot}`} aria-hidden="true" />
        <span style={{ display: "inline-flex", flexDirection: "column" }}>
          <span>{label}</span>
          {subline ? (
            <span style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {subline}
            </span>
          ) : null}
        </span>
        {overdue && <span className="order-status-pill-overdue">Overdue</span>}
      </span>
    );
  };

  const getActionButtons = (order) => {
    const rawStatus = order.status || "pending";
    const currentStatus = rawStatus === "order_placed" ? "pending" : rawStatus;

    const handleClick = async (newStatus) => {
      if (!restaurantId) return;
      setUpdatingOrderId(order.id + newStatus);

      try {
        const patch = await updateOrderStatus(restaurantId, order.id, newStatus);

        setOrders((prev) =>
          prev.map((o) =>
            o.id === order.id
              ? {
                  ...o,
                  ...patch,
                  status: newStatus,
                }
              : o
          )
        );
      } catch (error) {
        alert(getRestaurantOrderActionErrorMessage(error));
      } finally {
        setUpdatingOrderId("");
      }
    };

    const baseClasses =
      "rounded-xl px-3 py-1 text-xs font-medium transition disabled:cursor-not-allowed";

    if (currentStatus === "pending") {
      return (
        <button
          onClick={() => handleClick("order_confirmed")}
          disabled={updatingOrderId === order.id + "order_confirmed"}
          className={`${baseClasses} bg-black text-white hover:bg-gray-900 disabled:bg-gray-400`}
        >
          {updatingOrderId === order.id + "order_confirmed"
            ? "Updating…"
            : "Accept order"}
        </button>
      );
    }

    if (currentStatus === "order_confirmed") {
      return (
        <button
          onClick={() => handleClick("preparing")}
          disabled={updatingOrderId === order.id + "preparing"}
          className={`${baseClasses} bg-black text-white hover:bg-gray-900 disabled:bg-gray-400`}
        >
          {updatingOrderId === order.id + "preparing"
            ? "Updating…"
            : "Start preparing"}
        </button>
      );
    }

    if (currentStatus === "preparing" || currentStatus === "accepted") {
      const pickup = isPickupOrder(order);
      const nextStatus = pickup ? "ready_for_pickup" : "ready_for_driver";

      return (
        <button
          onClick={() => handleClick(nextStatus)}
          disabled={updatingOrderId === order.id + nextStatus}
          className={`${baseClasses} bg-black text-white hover:bg-gray-900 disabled:bg-gray-400`}
        >
          {updatingOrderId === order.id + nextStatus
            ? "Updating…"
            : pickup
            ? "Ready for pickup"
            : "Order ready"}
        </button>
      );
    }

    // ✅ pickup flow final step
    if (currentStatus === "ready_for_pickup") {
      return (
        <button
          onClick={async () => {
            if (!restaurantId) return;
            setUpdatingOrderId(order.id + "pickup_completed");

            try {
              const patch = await markPickupCompleted(restaurantId, order.id);

              setOrders((prev) =>
                prev.map((o) =>
                  o.id === order.id
                    ? {
                        ...o,
                        ...patch,
                        status: "completed",
                      }
                    : o
                )
              );
            } catch (error) {
              alert(getRestaurantOrderActionErrorMessage(error));
            } finally {
              setUpdatingOrderId("");
            }
          }}
          disabled={updatingOrderId === order.id + "pickup_completed"}
          className={`${baseClasses} bg-black text-white hover:bg-gray-900 disabled:bg-gray-400`}
        >
          {updatingOrderId === order.id + "pickup_completed"
            ? "Updating…"
            : "Mark picked up"}
        </button>
      );
    }

    return null;
  };

  if (restaurantLoading) {
    return (
      <ManagerShell
        title="Orders"
        subtitle="Review your restaurant’s orders in real time."
      >
        <p className="muted">Loading restaurant…</p>
      </ManagerShell>
    );
  }

  if (!restaurantId) {
    return (
      <ManagerShell
        title="Orders"
        subtitle="Review your restaurant’s orders in real time."
      >
        <p className="muted">No restaurant selected. Please sign in again.</p>
      </ManagerShell>
    );
  }

  return (
    <ManagerShell
      title="Orders"
      subtitle="Review your restaurant’s orders in real time."
    >
      <div className="orders-main">
        {ordersError && <div className="error-banner">{ordersError}</div>}

        {/* ✅ NEW: Scheduled reminder banner */}
        {scheduledReminderBanner && (
          <div className="orders-overdue-banner">
            {scheduledReminderBanner}
          </div>
        )}

        {overdueCount > 0 && (
          <div className="orders-overdue-banner">
            {overdueCount === 1
              ? "1 order has been waiting longer than expected. Please review it."
              : `${overdueCount} orders have been waiting longer than expected. Please review them.`}
          </div>
        )}

        <div className="orders-tabs">
          <button
            type="button"
            className={`orders-tab ${filter === "all" ? "active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All
          </button>

          {/* ✅ NEW: Scheduled tab */}
          <button
            type="button"
            className={`orders-tab ${filter === "scheduled" ? "active" : ""}`}
            onClick={() => setFilter("scheduled")}
            title="Scheduled orders (future / pending)"
          >
            Scheduled
            {scheduledFutureCount > 0 && (
              <span className="orders-tab-badge">{scheduledFutureCount}</span>
            )}
          </button>

          <button
            type="button"
            className={`orders-tab ${filter === "unfulfilled" ? "active" : ""}`}
            onClick={() => setFilter("unfulfilled")}
          >
            Unfulfilled
          </button>
          <button
            type="button"
            className={`orders-tab ${filter === "today" ? "active" : ""}`}
            onClick={() => setFilter("today")}
          >
            Today
          </button>
          <button
            type="button"
            className={`orders-tab ${filter === "in_progress" ? "active" : ""}`}
            onClick={() => setFilter("in_progress")}
          >
            In progress
            {overdueCount > 0 && (
              <span className="orders-tab-badge">{overdueCount}</span>
            )}
          </button>
          <button
            type="button"
            className={`orders-tab ${filter === "completed" ? "active" : ""}`}
            onClick={() => setFilter("completed")}
          >
            Completed
          </button>
        </div>

        <div className="orders-card">
          {ordersLoading ? (
            <p className="muted">Loading orders...</p>
          ) : filteredOrders.length === 0 ? (
            <div className="empty-state">
              <h3>No orders yet</h3>
              <p>When customers place orders, they will appear here in real time.</p>
            </div>
          ) : (
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Details</th>
                  <th>Status</th>
                  <th>Total</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredOrders.map((order) => {
                  const overdue = isOrderOverdue(order);

                  const pickup = isPickupOrder(order);
                  const st = (order.status || "").toLowerCase();

                  const deliveryAddress = pickup ? "" : order.deliveryAddress || "";
                  const cutleryLabel = getCutleryLabel(order);

                  const scheduleLine = getScheduleLine(order);

                  const { lat: deliveryLat, lng: deliveryLng } = extractLatLng(
                    order.deliveryLocation || {}
                  );

                  const { lat: driverLat, lng: driverLng } = extractLatLng(
                    order.driverLocation || null
                  );

                  const hasDeliveryCoords =
                    deliveryLat != null && deliveryLng != null;
                  const hasDriverCoords = driverLat != null && driverLng != null;

                  const customerMapsUrl =
                    !pickup && hasDeliveryCoords
                      ? `https://www.google.com/maps?q=${deliveryLat},${deliveryLng}`
                      : null;

                  const driverMapsUrl =
                    !pickup && hasDriverCoords
                      ? `https://www.google.com/maps?q=${driverLat},${driverLng}`
                      : null;

                  return (
                    <tr
                      key={order.id}
                      className={overdue ? "order-row overdue" : "order-row"}
                    >
                      <td>
                        <div className="order-number">{`#${deriveNumericCode(order)}`}</div>
                      </td>

                      <td>
                        <div className="order-customer">
                          {order.customerName ||
                            order.customer?.name ||
                            order.customerEmail ||
                            order.userId ||
                            "Customer"}
                        </div>

                        <div className="order-date">{formatDateTimeFromOrder(order)}</div>

                        {/* ✅ Scheduled vs ASAP line */}
                        <div className="order-date" style={{ marginTop: 4, fontWeight: 700 }}>
                          {scheduleLine}
                        </div>

                        {/* ✅ Pickup/Delivery label */}
                        <div
                          className="order-date"
                          style={{
                            marginTop: 6,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span
                            style={{
                              padding: "2px 10px",
                              borderRadius: 999,
                              fontSize: 12,
                              fontWeight: 700,
                              background: pickup
                                ? "rgba(16,185,129,0.12)"
                                : "rgba(59,130,246,0.10)",
                              color: pickup ? "#047857" : "#1d4ed8",
                            }}
                          >
                            {pickup ? "Pickup" : "Delivery"}
                          </span>

                          {/* ✅ Optional small Scheduled chip if scheduled */}
                          {isScheduledOrder(order) ? (
                            <span
                              style={{
                                padding: "2px 10px",
                                borderRadius: 999,
                                fontSize: 12,
                                fontWeight: 700,
                                background: "rgba(168,85,247,0.14)",
                                color: "#7c3aed",
                              }}
                            >
                              Scheduled
                            </span>
                          ) : null}
                        </div>

                        {cutleryLabel && (
                          <div className="order-date" style={{ marginTop: 6 }}>
                            {cutleryLabel}
                          </div>
                        )}

                        {deliveryAddress && (
                          <div className="order-address">{deliveryAddress}</div>
                        )}

                        {/* ✅ Hide this line once pickup is completed/cancelled */}
                        {pickup && st !== "completed" && st !== "cancelled" && (
                          <div className="order-date" style={{ marginTop: 6 }}>
                            Customer will pick up from your restaurant.
                          </div>
                        )}

                        {customerMapsUrl && (
                          <a
                            href={customerMapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="order-maps-link"
                          >
                            Open customer in Maps
                          </a>
                        )}

                        {driverMapsUrl && (
                          <a
                            href={driverMapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="order-maps-link"
                            style={{ marginLeft: 8 }}
                          >
                            View driver in Maps
                          </a>
                        )}
                      </td>

                      <td>{renderStatusCell(order, overdue)}</td>

                      <td>
                        {formatCurrency(order.total ?? order.totalAmount ?? order.subtotal)}
                      </td>

                      <td>
                        <div className="order-actions">
                          {getActionButtons(order)}

                          <button
                            type="button"
                            className="orders-edit-link"
                            onClick={() => printOrder(order)}
                            title={`Print order #${deriveNumericCode(order)}`}
                          >
                            Print
                          </button>

                          {(() => {
                            // ✅ Keep the old rule for Edit vs View
                            const editable = [
                              "pending",
                              "order_placed",
                              "order_confirmed",
                              "preparing",
                            ].includes(st);

                            return (
                              <button
                                type="button"
                                className="orders-edit-link"
                                onClick={() => {
                                  setEditingOrder(order);
                                  setIsEditorOpen(true);
                                }}
                              >
                                {editable ? "Edit" : "View"}
                              </button>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <OrderEditorDrawer
        open={isEditorOpen}
        order={editingOrder}
        restaurantId={restaurantId}
        onClose={() => {
          setIsEditorOpen(false);
          setEditingOrder(null);
        }}
        onSaved={(updated) => {
          setOrders((prev) =>
            prev.map((o) => (o.id === updated.id ? { ...o, ...updated } : o))
          );
        }}
      />
    </ManagerShell>
  );
};

export default OrdersPage;
